import { BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalAuthConfig } from '@shared/auth/config/msalAuthConfig/web';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider as TanstackRouterProvider } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import type { PropsWithChildren } from 'react';

import { axiosEventBus } from '@/api/eventBus';
import { GenericError } from '@/components/atoms/GenericError';
import { Loader } from '@/components/atoms/Loader';
import { NotFound } from '@/components/atoms/NotFound';
import { AxiosErrorHandler } from '@/components/AxiosErrorHandler';
import { NewVersionNotifier } from '@/components/NewVersionNotifier';
import { AuthProvider, useAuth } from '@/context/auth';
import { EstimationProvider } from '@/context/estimationContext';
import { EstimationWizardProvider } from '@/context/estimationWizardContext';
import { LocalizationProvider } from '@/context/localization';
import { ModalProvider } from '@/context/modal';
import { InitializationProvider } from '@/context/system/initialization';
import { ProcessMonitorProvider } from '@/context/system/processMonitor';
import { ThemeProvider } from '@/context/theme';
import { TenantStyleInjector } from '@/context/theme/TenantStyleInjector';
import { ToastProvider } from '@/context/toast';
import { routeTree } from '@/routeTree.gen.ts';

import { AiSettingsProvider } from '../context/aiSettings';
import { AppInsightsContextProvider } from './appInsightsContextProvider';

const msalInstance = new PublicClientApplication(
  msalAuthConfig(BrowserCacheLocation.SessionStorage),
);
// Retrying 4xx is pointless and amplifies noise — a single forbidden user generated 160 retries
// in two days against /tenant/export-config in prod. Stop retrying client-side failures.
const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status !== undefined && status >= 400 && status < 500) {
      return false;
    }
  }
  return failureCount < 1;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
    mutations: {
      throwOnError: false,
    },
  },
});
const router = createRouter({
  context: { isAuthenticated: false },
  routeTree,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const RouterProvider = () => {
  const { auth } = useAuth();

  return (
    <TanstackRouterProvider
      router={router}
      context={{ isAuthenticated: Boolean(auth) }}
      key={auth ? 'authenticated' : 'unauthenticated'}
      defaultErrorComponent={GenericError}
      defaultPendingComponent={Loader}
      defaultNotFoundComponent={NotFound}
    />
  );
};

const InfraProviders = ({ children }: PropsWithChildren) => (
  <ProcessMonitorProvider>
    <MsalProvider instance={msalInstance}>
      <AuthProvider emit={axiosEventBus.emit}>
        <ToastProvider>
          <AppInsightsContextProvider>{children}</AppInsightsContextProvider>
        </ToastProvider>
      </AuthProvider>
    </MsalProvider>
  </ProcessMonitorProvider>
);

const DataProviders = ({ children }: PropsWithChildren) => (
  <QueryClientProvider client={queryClient}>
    <ReactQueryDevtools initialIsOpen={false} />
    <LocalizationProvider emit={axiosEventBus.emit}>
      <AxiosErrorHandler
        subscribe={axiosEventBus.subscribe}
        unsubscribe={axiosEventBus.unsubscribe}
      />
      <NewVersionNotifier
        subscribe={axiosEventBus.subscribe}
        unsubscribe={axiosEventBus.unsubscribe}
      />
      {children}
    </LocalizationProvider>
  </QueryClientProvider>
);

const AppProviders = ({ children }: PropsWithChildren) => (
  <InitializationProvider>
    <AiSettingsProvider>
      <EstimationProvider>
        <EstimationWizardProvider>
          <TenantStyleInjector>
            <ThemeProvider>
              <ModalProvider>{children}</ModalProvider>
            </ThemeProvider>
          </TenantStyleInjector>
        </EstimationWizardProvider>
      </EstimationProvider>
    </AiSettingsProvider>
  </InitializationProvider>
);

export const Providers = () => (
  <InfraProviders>
    <DataProviders>
      <AppProviders>
        <RouterProvider />
      </AppProviders>
    </DataProviders>
  </InfraProviders>
);
